import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { useUserAPI } from '../../contexts/UserAPIContext';
import { useNotification } from '../../contexts/NotificationContext';
import CustomAutoComplete from "../../components/CustomAutoComplete";
import DateDisplay from "../../components/DateDisplay";
import { getImageUrl } from '../../firebase/storageService'; // função criada acima
import PageLoader from '../../components/PageLoader';
import TableLoader from '../../components/TableLoader';
import { authFetch } from "../../utils/authFetch";
import { applyPhoneMask, applyZipCodeMask, applyCpfMask, fetchAddressByZipCode } from '../../utils/masks';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Função para converter URLs em hiperlinks
const convertLinksToHtml = (text) => {
  if (!text) return '';

  // Regex para detectar URLs (http, https, www)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi;

  // Substitui as URLs por links HTML
  return text.replace(urlRegex, (url) => {
    // Se a URL não começar com http:// ou https://, adiciona https://
    const href = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    // Adiciona target="_blank" e rel="noopener noreferrer" para segurança
    return `<a href="${href}" 
              target="_blank" 
              rel="noopener noreferrer" 
              onclick="window.open(this.href, '_blank'); return false;" 
              style="color: #47a0be; text-decoration: underline; cursor: pointer;">${url}</a>`;
  });
};

const PerfilPaciente = () => {

  const { id } = useParams();
  const { userAPIData } = useUserAPI();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const [paciente, setPaciente] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [curriculos, setCurriculos] = useState([]);
  const [descricaoCurriculo, setDescricaoCurriculo] = useState("");
  const [anotacao, setAnotacao] = useState("");
  const [descricaoAnotacao, setDescricaoAnotacao] = useState("");
  const [linhadotempo, setLinhadotempo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModalPlanosMes, setShowModalPlanosMes] = useState(false);
  const [fotoResponsavelModal, setFotoResponsavelModal] = useState(null);
  const [responsaveisLoaded, setResponsaveisLoaded] = useState(false);
  const [loadingResponsaveis, setLoadingResponsaveis] = useState(false);
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  const [planosLoaded, setPlanosLoaded] = useState(false);
  const [curriculoLoaded, setCurriculoLoaded] = useState(false);
  const [observacaoLoaded, setObservacaoLoaded] = useState(false);
  const [editingCurriculum, setEditingCurriculum] = useState(false);
  const [editingObservation, setEditingObservation] = useState(false);

  const [imagePacienteUrl, setImagePacienteUrl] = useState(null);

  const turnoLabels = {
    0: 'Manhã',
    1: 'Tarde'
  };

  const calcularIdade = (birthdateStr) => {
    if (!birthdateStr) return 'Não informado';

    const [year, month, day] = birthdateStr.split('-').map(Number);
    if (!year || !month || !day) return 'Não informado';

    const birthDate = new Date(year, month - 1, day);
    if (Number.isNaN(birthDate.getTime())) return 'Não informado';

    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();

    if (today.getDate() < birthDate.getDate()) {
      months -= 1;
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    const anosLabel = years === 1 ? ' Ano' : ' Anos';
    const mesesLabel = months === 1 ? ' Mês' : ' Meses';

    return `${years}${anosLabel}${months > 0 ? ` e ${months}${mesesLabel}` : ''}`.trim();
  };

  const [anoRef, setAnoRef] = useState(Number(new Date().getFullYear()));
  const [mesRef, setMesRef] = useState(() => {
    const mesAtual = new Date().getMonth() + 1; // getMonth retorna de 0 a 11
    return String(mesAtual).padStart(2, '0'); // formata para '01', '02' etc.
  });
  const meses = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const [mesFechadoCtrl, setMesFechadoCtrl] = useState(false);

  const [idadeFormatada, setIdadeFormatada] = useState("");

  const [planoId, setPlanoId] = useState('');
  const [tipoPlano, setTipoPlano] = useState('');
  const [valorAcordado, setValorAcordado] = useState('');
  const [descricaoPlano, setDescricaoPlano] = useState('');
  const [mostrarAddPlano, setMostrarAddPlano] = useState(false);
  const [listPlanos, setListPlanos] = useState([]);

  const [editandoPlanoId, setEditandoPlanoId] = useState(null);
  const [valorEditado, setValorEditado] = useState('');
  const [descricaoEditada, setDescricaoEditada] = useState('');
  const [allowReschedulePlano, setAllowReschedulePlano] = useState(false);
  const [allowRescheduleEditado, setAllowRescheduleEditado] = useState(false);

  // Refs para os campos contentEditable
  const curriculoRef = useRef(null);
  const observacaoRef = useRef(null);

  const iniciarEdicaoPlano = (plano) => {
    setEditandoPlanoId(plano.id);
    setValorEditado(formatarValor(plano.contractValue)); // já formatado
    setDescricaoEditada(plano.description ?? '');
    setAllowRescheduleEditado(plano.allowReschedule ?? false);
  };

  const MostrarDetalhesPagMes = () => {
    buscarEventosDetalhados();
    setShowModalPlanosMes(true);
  }

  // Buscar currículos vinculados ao paciente
  const buscarCurriculos = async () => {
    try {
      const response = await authFetch(`patient-record/patient/${id}?year=${anoRef}`, {
      });
      const data = await response.json();
      setCurriculos(data);
      setDescricaoCurriculo(data.description || "");
    } catch (error) {
      setCurriculos("");
      setDescricaoCurriculo("");
    }
  }

  const buscarAnotacoes = async () => {
    try {
      const response = await authFetch(`patient-note/patient/${id}?year=${anoRef}`, {
      });
      const data = await response.json();
      if (data && data.length > 0 && data[0]) {
        setAnotacao(data[0]);
        setDescricaoAnotacao(data[0].note || "");
      } else {
        setAnotacao("");
        setDescricaoAnotacao("");
      }
    } catch (error) {
      setAnotacao("");
      setDescricaoAnotacao("");
    }
  }

  // Buscar planos vinculados ao paciente
  const buscarPlanos = async () => {
    const response = await authFetch(`patient-plan/patient/${id}`, {
      headers: {

        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    setPlanos(data);
  }

  // Buscar linha do tempo do paciente
  const buscarLinhaDoTempo = async () => {
    const response = await authFetch(`patient-event/patient/${id}?year=${anoRef}`, {
      headers: {

        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    setLinhadotempo(data);
  }

  const buscarResponsaveisPaciente = async () => {
    setLoadingResponsaveis(true);
    try {
      // Buscar responsaveis do paciente
      const response = await authFetch(`patient-guardian/patient/${id}`, {
      });
      const data = await response.json();

      // Para cada responsável, buscar imagem e anexar no objeto
      const responsaveisComImagem = await Promise.all(
        data.map(async (responsavel) => {
          const url = await getImageUrl('guardian', responsavel.id); // pasta correta
          const email = responsavel.guardian?.email;
          // Fonte de verdade: o e-mail existir na tabela de usuários.
          // O userId no guardian pode estar órfão (usuário removido sem limpar o vínculo).
          let hasLogin = false;
          if (email) {
            try {
              const res = await authFetch(`user/exists?email=${encodeURIComponent(email)}`, {
                method: 'GET',
              });
              hasLogin = res.ok ? await res.json() : !!responsavel.guardian?.userId;
            } catch (e) {
              // se a checagem falhar, usa o userId como fallback
              hasLogin = !!responsavel.guardian?.userId;
            }
          }
          return {
            ...responsavel,
            imageUrl: url,
            hasLogin,
          };
        })
      );
      setResponsaveis(responsaveisComImagem);
    } catch (error) {
      console.error('Erro ao buscar responsáveis:', error);
      showNotification('danger', 'Erro ao carregar responsáveis');
    } finally {
      setLoadingResponsaveis(false);
    }
  }

  const handleResponsaveisClick = async () => {
    if (!responsaveisLoaded) {
      await buscarResponsaveisPaciente();
      setResponsaveisLoaded(true);
    }
  };

  // Ativar aba de responsáveis quando vier de edição de responsável
  useEffect(() => {
    if (location.state?.tab === 'responsaveis') {
      // Aguardar um pouco para garantir que o DOM está pronto
      const timer = setTimeout(() => {
        const tabElement = document.querySelector('#nav-responsaveis-tab');
        if (tabElement) {
          // Disparar o clique na aba para ativá-la
          tabElement.click();
          // Limpar o state após usar sem causar re-render
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleTimelineClick = async () => {
    if (!timelineLoaded) {
      await buscarLinhaDoTempo();
      setTimelineLoaded(true);
    }
  };

  const handlePlanosClick = async () => {
    if (!planosLoaded) {
      await fetchControlePagamento();
      setPlanosLoaded(true);
    }
  };

  const handleCurriculoClick = async () => {
    if (!curriculoLoaded) {
      await buscarCurriculos();
      setCurriculoLoaded(true);
    }
  };

  const handleObservacoesClick = async () => {
    if (!observacaoLoaded) {
      await buscarAnotacoes();
      setObservacaoLoaded(true);
    }
  }

  const fetchControlePagamento = async () => {
    try {
      const response = await authFetch(`patient-payment-control/patient/${id}/month/${mesRef}/year/${anoRef}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar controle de pagamento');
      }
      const data = await response.json();
      setControlePagamento(data);
      setMesFechadoCtrl(data.monthClosed);      

      if (data.monthClosed === false || data.monthClosed === null) {
        const total = (data?.paymentControlCalculated?.totalMonthlyPlan || 0) + (data?.paymentControlCalculated?.totalHourlyPlan || 0) + (data?.paymentControlCalculated?.totalSingle || 0);        
        setTotalMensalidade(total);
      }

    } catch (error) {
      console.error('Erro ao buscar controle de pagamento:', error);
      showNotification('Erro ao buscar controle de pagamento', 'error');
    }
  };

  // Remove the useEffect that was watching anoRef and mesRef
  useEffect(() => {
    if (planosLoaded) {
      fetchControlePagamento();
    }
  }, [anoRef, mesRef]);

  useEffect(() => {
    if (curriculoLoaded) {
      buscarCurriculos();
    }
    if (observacaoLoaded) {
      buscarAnotacoes();
    }
    if (timelineLoaded) {
      buscarLinhaDoTempo();
    }
  }, [anoRef]);

  // UseEffect para atualizar o campo de currículo quando descricaoCurriculo mudar
  useEffect(() => {
    if (curriculoRef.current && descricaoCurriculo !== undefined) {
      const htmlContent = descricaoCurriculo ? convertLinksToHtml(descricaoCurriculo).replace(/\n/g, '<br/>') : '';
      if (curriculoRef.current.innerHTML !== htmlContent) {
        curriculoRef.current.innerHTML = htmlContent;
      }
    }
  }, [descricaoCurriculo]);

  // UseEffect para atualizar o campo de observação quando descricaoAnotacao mudar
  useEffect(() => {
    if (observacaoRef.current && descricaoAnotacao !== undefined) {
      const htmlContent = descricaoAnotacao ? convertLinksToHtml(descricaoAnotacao).replace(/\n/g, '<br/>') : '';
      if (observacaoRef.current.innerHTML !== htmlContent) {
        observacaoRef.current.innerHTML = htmlContent;
      }
    }
  }, [descricaoAnotacao]);



  useEffect(() => {
    const fetchPaciente = async () => {
      try {
        // Primeiro carrega os dados do paciente
        const response = await authFetch(`patient/${id}`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error('Erro ao buscar paciente');

        const data = await response.json();
        setPaciente(data);
        setLoading(false); // Marca como carregado assim que tiver os dados principais

        // Depois carrega a imagem em background
        try {
          const url = await getImageUrl('patient', id);
          setImagePacienteUrl(url);
        } catch (error) {
          console.error('Erro ao carregar imagem:', error);
          setImagePacienteUrl(require('../../assets/img/perfilvazio.png'));
        }

        // Carrega os planos em background
        buscarPlanos();

      } catch (err) {
        console.error(err);
        showNotification("danger", "Erro ao buscar paciente");
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPaciente();
  }, [id, mostrarAddPlano]);

  useEffect(() => {
    if (paciente && paciente.birthdate) {
      setIdadeFormatada(calcularIdade(paciente.birthdate));
    }
  }, [paciente]);

  // quando carregar o paciente
  // useEffect(() => {
  //   if (paciente && paciente.note !== undefined) {
  //     setAnotacao(paciente.note);
  //   }
  // }, [paciente]);


  const [showConfirmDeletePaciente, setShowConfirmDeletePaciente] = useState(false);

  const handleDeletePaciente = async () => {
    try {
      const response = await authFetch(`patient/${paciente.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Erro ao excluir paciente');

      showNotification('success', 'Paciente excluído com sucesso');
      navigate(location.state?.fromBeta ? '/PacientesBeta' : '/Pacientes');
    } catch (err) {
      console.error(err);
      showNotification('danger', 'Erro ao excluir paciente');
    } finally {
      setShowConfirmDeletePaciente(false);
    }
  };


  // Funções Responsáveis
  const [showFormResponsaveis, setShowFormResponsaveis] = useState(false);
  const [editandoResponsavel, setEditandoResponsavel] = useState(null); // Objeto com dados do responsável sendo editado
  const [visualizandoResponsavel, setVisualizandoResponsavel] = useState(null); // Objeto com dados do responsável sendo visualizado

  const [responsavelParaExcluir, setResponsavelParaExcluir] = useState(null);
  const [showModalExcluir, setShowModalExcluir] = useState(false);

  const [linhaTempoParaExcluir, setLinhaTempoParaExcluir] = useState(null);
  const [showModalExclusaoLT, setShowModalExclusaoLT] = useState(false);

  const confirmarExclusaoResponsavel = (responsavel) => {
    setResponsavelParaExcluir(responsavel);
    setShowModalExcluir(true);
  };

  // Função para abrir visualização de responsável
  const handleVisualizarResponsavel = async (responsavel) => {
    try {
      // Buscar dados completos do responsável
      const response = await authFetch(`guardian/${responsavel.guardian.id}`, {
      });
      
      if (!response.ok) throw new Error("Erro ao buscar dados do responsável");
      
      const guardianData = await response.json();
      
      // Preencher dados de visualização
      setVisualizandoResponsavel({
        ...guardianData,
        guardianType: responsavel.guardianType,
        patientGuardianId: responsavel.id
      });
      
      setShowFormResponsaveis(false);
      setEditandoResponsavel(null);
    } catch (error) {
      console.error(error);
      showNotification("danger", "Erro ao carregar dados do responsável");
    }
  };

  // Função para abrir edição de responsável
  const handleEditarResponsavel = async (responsavel) => {
    try {
      // Buscar dados completos do responsável
      const response = await authFetch(`guardian/${responsavel.guardian.id}`, {
      });
      
      if (!response.ok) throw new Error("Erro ao buscar dados do responsável");
      
      const guardianData = await response.json();
      
      // Formatar CPF e CEP para exibição
      const cpfFormatado = guardianData.cpf ? applyCpfMask(String(guardianData.cpf).replace(/\D/g, '')) : '';
      const zipCodeFormatado = guardianData.zipCode ? applyZipCodeMask(String(guardianData.zipCode).replace(/\D/g, '')) : '';
      
      // Preencher formulário de edição
      setEditResponsavel({
        id: guardianData.id,
        guardianId: guardianData.id,
        patientGuardianId: responsavel.id,
        guardianType: responsavel.guardianType || "",
        name: guardianData.name || "",
        email: guardianData.email || "",
        cpf: cpfFormatado,
        zipCode: zipCodeFormatado,
        address: guardianData.address || "",
        neighborhood: guardianData.neighborhood || "",
        addressNumber: guardianData.addressNumber || "",
        addressComplement: guardianData.addressComplement || "",
        state: guardianData.state || "",
        city: guardianData.city || "",
        phone: guardianData.phone ? applyPhoneMask(String(guardianData.phone).replace(/\D/g, '')) : "",
        cellphone: guardianData.cellphone ? applyPhoneMask(String(guardianData.cellphone).replace(/\D/g, '')) : ""
      });
      
      setEditandoResponsavel(responsavel);
      setShowFormResponsaveis(false);
      setVisualizandoResponsavel(null);
    } catch (error) {
      console.error(error);
      showNotification("danger", "Erro ao carregar dados do responsável");
    }
  };

  // Função para fechar visualização/edição
  const handleFecharVisualizarEditar = () => {
    setVisualizandoResponsavel(null);
    setEditandoResponsavel(null);
  };

  // Handler para mudanças no formulário de edição
  const handleInputChangeEditResp = (e) => {
    let { name, value } = e.target;

    // Aplica máscaras conforme o campo
    if (name === 'phone' || name === 'cellphone') {
      value = applyPhoneMask(value);
    } else if (name === 'zipCode') {
      value = applyZipCodeMask(value);
    } else if (name === 'cpf') {
      value = applyCpfMask(value);
    }

    setEditResponsavel((prev) => {
      return { ...prev, [name]: value };
    });
  };

  // Busca endereço por CEP no formulário de edição
  const handleZipCodeBlurEdit = async () => {
    if (editResponsavel.zipCode.length === 9) {
      const address = await fetchAddressByZipCode(editResponsavel.zipCode);
      if (address) {
        setEditResponsavel((prev) => ({
          ...prev,
          address: address.logradouro || '',
          neighborhood: address.bairro || '',
          state: address.uf || '',
          city: address.localidade || ''
        }));
      }
    }
  };

  // Submit do formulário de edição de responsável
  const handleEditResponsavel = async (e) => {
    e.preventDefault();

    try {
      // Atualizar o responsável
      const formToSubmit = {
        id: editResponsavel.id,
        name: editResponsavel.name,
        email: editResponsavel.email || null,
        cpf: editResponsavel.cpf.replace(/\D/g, '') || null,
        zipCode: editResponsavel.zipCode.replace(/\D/g, '') || null,
        address: editResponsavel.address || null,
        neighborhood: editResponsavel.neighborhood || null,
        addressNumber: editResponsavel.addressNumber || null,
        addressComplement: editResponsavel.addressComplement || null,
        state: editResponsavel.state || null,
        city: editResponsavel.city || null,
        phone: editResponsavel.phone.replace(/\D/g, '') || null,
        cellphone: editResponsavel.cellphone.replace(/\D/g, '') || null,
        userId: null
      };

      const responseGuardian = await authFetch(`guardian/${editResponsavel.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formToSubmit),
      });

      if (!responseGuardian.ok) {
        throw new Error('Erro ao atualizar responsável');
      }

      // Validação: parentesco é obrigatório
      if (!editResponsavel.guardianType || !editResponsavel.guardianType.trim()) {
        showNotification("danger", "Parentesco é obrigatório");
        return;
      }

      // Atualizar o relacionamento patient-guardian com o parentesco
      if (editResponsavel.patientGuardianId && editResponsavel.guardianType.trim()) {
        try {
          const updateResponse = await authFetch(`patient-guardian/${editResponsavel.patientGuardianId}`, {
            method: "PUT",
            body: JSON.stringify({
              id: editResponsavel.patientGuardianId,
              patientId: paciente.id,
              guardianId: editResponsavel.guardianId,
              guardianType: editResponsavel.guardianType.trim()
            }),
          });
          
          if (!updateResponse.ok) {
            console.error("Erro ao atualizar parentesco no relacionamento patient-guardian");
            showNotification("warning", "Responsável atualizado, mas houve erro ao atualizar o parentesco");
          }
        } catch (error) {
          console.error("Erro ao atualizar relacionamento patient-guardian:", error);
          showNotification("warning", "Responsável atualizado, mas houve erro ao atualizar o parentesco");
        }
      }

      showNotification("success", "Responsável atualizado com sucesso!");
      setEditandoResponsavel(null);
      buscarResponsaveisPaciente();
    } catch (error) {
      console.error(error);
      showNotification("danger", "Erro ao atualizar responsável");
    }
  };

  const excluirLinhaDoTempo = (lt) => {
    setLinhaTempoParaExcluir(lt);
    setShowModalExclusaoLT(true);
  };

  const excluirLinhaDoTempoConfirmado = async () => {
    if (!linhaTempoParaExcluir) return;

    try {
      const res = await authFetch(`patient-event/${linhaTempoParaExcluir.id}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Erro ao excluir Linha do Tempo");

      showNotification("success", "Linha do Tempo excluído com sucesso!");
      setShowModalExclusaoLT(false);
      setLinhaTempoParaExcluir(null);
      buscarLinhaDoTempo(); // atualiza lista
    } catch (error) {
      console.error("Erro ao excluir Linha do Tempo:", error);
      showNotification("danger", "Erro ao excluir Linha do Tempo");
      setShowModalExclusaoLT(false);
    }
  };


  const handleExcluirResponsavel = async () => {
    if (!responsavelParaExcluir) return;

    try {
      const response = await authFetch(`patient-guardian/${responsavelParaExcluir.id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Erro ao excluir responsável");
      }

      // Remove da lista local
      setResponsaveis((prev) =>
        prev.filter((r) => r.paciente_responsavel_id !== responsavelParaExcluir.paciente_responsavel_id)
      );

      buscarResponsaveisPaciente();
      showNotification("success", "Responsável excluído com sucesso!");
    } catch (error) {
      console.error(error);
      showNotification("danger", "Erro ao excluir responsável");
    } finally {
      setShowModalExcluir(false);
      setResponsavelParaExcluir(null);
    }
  };

  const [novoResponsavel, setNovoResponsavel] = useState({
    guardianId: "",
    guardianType: "",
    patientId: id,
    name: '',
    email: '',
    cpf: '',
    zipCode: '',
    address: '',
    neighborhood: '',
    addressNumber: '',
    addressComplement: '',
    state: '',
    city: '',
    phone: '',
    cellphone: ''
  });

  // Estado para o formulário de edição de responsável
  const [editResponsavel, setEditResponsavel] = useState({
    id: null,
    guardianId: null,
    patientGuardianId: null,
    guardianType: "",
    name: '',
    email: '',
    cpf: '',
    zipCode: '',
    address: '',
    neighborhood: '',
    addressNumber: '',
    addressComplement: '',
    state: '',
    city: '',
    phone: '',
    cellphone: ''
  });

  // Função que busca responsáveis para o autocomplete (por nome)
  const buscarResponsaveis = async (termo) => {
    const res = await authFetch(`guardian?name=${encodeURIComponent(termo)}`, {
    });
    if (!res.ok) throw new Error("Erro ao buscar responsáveis");
    return res.json();
  };

  // Preenche os campos do formulário conforme digitação
  const handleInputChangeResp = (e) => {
    let { name, value } = e.target;

    // Aplica máscaras conforme o campo
    if (name === 'phone' || name === 'cellphone') {
      value = applyPhoneMask(value);
    } else if (name === 'zipCode') {
      value = applyZipCodeMask(value);
    } else if (name === 'cpf') {
      value = applyCpfMask(value);
    }

    setNovoResponsavel((prev) => {
      return { ...prev, [name]: value };
    });
  };

  // Preenche os campos quando um responsável é selecionado no autocomplete
  const handleSelectResponsavel = (item) => {
    // Garante que o CPF e CEP venham formatados (caso venham como string numérica)
    const cpfFormatado = item.cpf ? applyCpfMask(String(item.cpf).replace(/\D/g, '')) : '';
    const zipCodeFormatado = item.zipCode ? applyZipCodeMask(String(item.zipCode).replace(/\D/g, '')) : '';
    
    setNovoResponsavel((prev) => ({
      ...prev,
      guardianId: item.id,
      name: item.name || '',
      email: item.email || '',
      cpf: cpfFormatado,
      zipCode: zipCodeFormatado,
      address: item.address || '',
      neighborhood: item.neighborhood || '',
      addressNumber: item.addressNumber || '',
      addressComplement: item.addressComplement || '',
      state: item.state || '',
      city: item.city || '',
      phone: item.phone ? applyPhoneMask(String(item.phone).replace(/\D/g, '')) : '',
      cellphone: item.cellphone ? applyPhoneMask(String(item.cellphone).replace(/\D/g, '')) : '',
    }));
    showNotification('success', 'Responsável encontrado! Dados preenchidos automaticamente.');
  };

  // Busca endereço por CEP
  const handleZipCodeBlur = async () => {
    if (novoResponsavel.zipCode.length === 9) {
      const address = await fetchAddressByZipCode(novoResponsavel.zipCode);
      if (address) {
        setNovoResponsavel((prev) => ({
          ...prev,
          address: address.logradouro || '',
          neighborhood: address.bairro || '',
          state: address.uf || '',
          city: address.localidade || ''
        }));
      }
    }
  };

  // Submit do formulário de adicionar responsável
  const handleAddResponsavel = async (e) => {
    e.preventDefault();

    let guardianId = novoResponsavel.guardianId;

    // Se não tem guardianId, precisa criar um novo responsável
    if (!guardianId) {
      // Validação: nome é obrigatório
      if (!novoResponsavel.name.trim()) {
        showNotification("danger", "Nome do responsável é obrigatório");
        return;
      }

      // Prepara o payload para criar o responsável
      const formToSubmit = {
        name: novoResponsavel.name,
        email: novoResponsavel.email || null,
        cpf: novoResponsavel.cpf.replace(/\D/g, '') || null,
        zipCode: novoResponsavel.zipCode.replace(/\D/g, '') || null,
        address: novoResponsavel.address || null,
        neighborhood: novoResponsavel.neighborhood || null,
        addressNumber: novoResponsavel.addressNumber || null,
        addressComplement: novoResponsavel.addressComplement || null,
        state: novoResponsavel.state || null,
        city: novoResponsavel.city || null,
        phone: novoResponsavel.phone.replace(/\D/g, '') || null,
        cellphone: novoResponsavel.cellphone.replace(/\D/g, '') || null,
        userId: null
      };

      try {
        // Cria o responsável
        const responseGuardian = await authFetch('guardian', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formToSubmit),
        });

        if (!responseGuardian.ok) {
          throw new Error('Erro ao cadastrar responsável');
        }

        const novoGuardian = await responseGuardian.json();
        guardianId = novoGuardian.id;
      } catch (error) {
        console.error(error);
        showNotification("danger", "Erro ao cadastrar responsável");
        return;
      }
    }

    // Validação: parentesco é obrigatório
    if (!novoResponsavel.guardianType || !novoResponsavel.guardianType.trim()) {
      showNotification("danger", "Parentesco é obrigatório");
      return;
    }

    // Agora vincula o responsável ao paciente
    const payload = {
      guardianId: guardianId,
      patientId: novoResponsavel.patientId || paciente.id,
      guardianType: novoResponsavel.guardianType.trim(),
    };

    try {
      const response = await authFetch("patient-guardian", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Erro ao adicionar responsável");
      }

      showNotification("success", "Responsável adicionado com sucesso!");
      setShowFormResponsaveis(false);
      setEditandoResponsavel(null);
      setVisualizandoResponsavel(null);

      // Resetando o estado após o sucesso
      setNovoResponsavel({
        guardianId: "",
        guardianType: "",
        patientId: paciente.id || "",
        name: '',
        email: '',
        cpf: '',
        zipCode: '',
        address: '',
        neighborhood: '',
        addressNumber: '',
        addressComplement: '',
        state: '',
        city: '',
        phone: '',
        cellphone: ''
      });
      buscarResponsaveisPaciente();

    } catch (error) {
      console.error(error);
      showNotification("danger", "Erro ao adicionar responsável");
    }
  };

  // Fim Funções Responsáveis

  // Funções Anotações
  const handleSubmitFormAnotacao = async (e) => {
    e.preventDefault();

    const method = anotacao && anotacao.id ? "PUT" : "POST";

    const payload = {
      note: descricaoAnotacao,
      year: anoRef,
      patientId: id,
      ...(anotacao?.id && { id: anotacao.id }) // só adiciona se existir
    };

    try {
      const response = await authFetch('patient-note', {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Erro ao salvar observação');

      const data = await response.json();
      setAnotacao(data);
      showNotification('success', 'Observação salva com sucesso!');
      buscarAnotacoes();
      setEditingObservation(false);

    } catch (error) {
      console.error('Erro ao salvar observação:', error);
      showNotification('danger', 'Erro ao salvar observação');
    }
  };

  // Fim Funções Anotações

  // Funções Currículo  
  const handleSubmitCurriculo = async (e) => {
    e.preventDefault();

    const method = curriculos.id ? "PUT" : "POST";

    const payload = {
      description: descricaoCurriculo,
      patientId: Number(id),
      highlightedSkill: "",
      developingSkill: "",
      obstacles: "",
      goals: "",
      emotionalProfile: "",
      clinicalReasoning: "",
      asdSymptoms: "",
      recordYear: Number(anoRef),
      ...(curriculos.id && { id: curriculos.id }) // só adiciona se existir
    };

    try {
      const res = await authFetch("patient-record", {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao salvar Currículo");

      buscarCurriculos();
      showNotification("success", "Currículo salvo com sucesso!");

    } catch (error) {
      console.error("Erro ao salvar currículo:", error);
      showNotification("danger", "Erro ao salvar currículo");
    }
  };


  // Fim Funções Currículo

  // Funções Linha do Tempo  
  const [novaLinhadotempo, setNovaLinhadotempo] = useState({
    patientId: id,
    date: "",
    description: ""
  });

  const [editandoLTId, setEditandoLTId] = useState(null);
  const [ltEditando, setLtEditando] = useState({
    date: "",
    description: ""
  });

  const iniciarEdicao = (lt) => {
    setEditandoLTId(lt.id);
    setLtEditando({ date: lt.date, description: lt.description });
  };

  const cancelarEdicao = () => {
    setEditandoLTId(null);
    setLtEditando({ date: "", description: "" });
  };

  const handleChangeEdicao = (e) => {
    const { name, value } = e.target;
    setLtEditando((prev) => ({ ...prev, [name]: value }));
  };

  // Preenche os campos do formulário conforme digitação
  const handleInputChangeLT = (e) => {
    const { name, value } = e.target;
    setNovaLinhadotempo((prev) => {
      return { ...prev, [name]: value }; // Atualizando apenas o campo modificado
    });
  };

  const handleSubmitLinhaDoTempo = async (e) => {
    e.preventDefault();

    const payload = {
      patientId: id,
      date: novaLinhadotempo.date,
      description: novaLinhadotempo.description
    };

    try {
      const res = await authFetch("patient-event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao salvar Linha do Tempo");

      showNotification("success", "Linha do Tempo salvo com sucesso!");

      // Atualiza o estado para refletir os dados mais recentes
      setNovaLinhadotempo({
        patientId: id,
        date: "",
        description: ""
      })
      buscarLinhaDoTempo();
    } catch (error) {
      console.error("Erro ao salvar Linha do Tempo:", error);
      showNotification("danger", "Erro ao salvar Linha do Tempo");
    }
  }

  const salvarEdicaoLT = async (idLT) => {
    const payload = {
      id: idLT,
      patientId: id,
      ...ltEditando,
    }

    try {
      const res = await authFetch("patient-event", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao editar Linha do Tempo");

      showNotification("success", "Linha do Tempo atualizada com sucesso!");

      // Atualiza a lista localmente com os dados corretos
      setLinhadotempo((prev) =>
        prev.map((item) =>
          item.id === idLT ? { ...item, ...ltEditando } : item
        )
      );

      cancelarEdicao();
    } catch (error) {
      console.error("Erro ao salvar edição:", error);
      showNotification("danger", "Erro ao salvar edição");
    }
  };
  // Fim Funções Linha do Tempo

  // Funções Planos
  const handleSubmitPlanos = async (e) => {
    e.preventDefault();

    const payload = {
      patientId: Number(id),
      planId: Number(planoId),
      contractValue: Number(valorAcordado.replace(/\./g, '').replace(',', '.')),
      description: descricaoPlano,
      allowReschedule: allowReschedulePlano
    };

    try {
      const res = await authFetch("patient-plan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao vincular plano");

      showNotification("success", "Plano vinculado com sucesso!");
      setMostrarAddPlano(false);
    } catch (error) {
      console.error("Erro ao vincular plano:", error);
      showNotification("danger", "Erro ao vincular plano");
    }
  }

  useEffect(() => {
    const fetchPlanos = async () => {
      try {
        const response = await authFetch("plan", {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error('Erro ao buscar planos');
        }

        const data = await response.json();
        setListPlanos(data);

        if (data.length > 0) {
          const primeiroPlano = data[0];
          setPlanoId(primeiroPlano.id);
          setTipoPlano(primeiroPlano);
        }

      } catch (error) {
        console.error('Erro no fetch de planos:', error);
        showNotification('danger', 'Erro ao carregar planos');
      }
    };

    fetchPlanos();
  }, [mostrarAddPlano]);

  const handleExcluirPlano = async (idPlano) => {
    if (!window.confirm("Deseja realmente excluir este plano vinculado?")) return;

    try {
      const response = await authFetch(`patient-plan/${idPlano}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao excluir plano vinculado');
      }

      // Remove da lista atual
      setPlanos(prev => prev.filter(p => p.id !== idPlano));
      showNotification("success", "Plano desvinculado com sucesso");
    } catch (error) {
      console.error(error);
      showNotification("danger", "Erro ao excluir plano vinculado");
    }
  };

  const cancelarEdicaoPlano = () => {
    setEditandoPlanoId(null);
    setValorEditado('');
    setDescricaoEditada('');
    setAllowRescheduleEditado(false);
  };


  const salvarEdicaoPlano = async (idPlano, idPlanVinculado) => {
    try {
      const body = {
        id: idPlanVinculado,
        patientId: id,
        planId: idPlano,
        contractValue: parseFloat(valorEditado.replace(/\D/g, '')) / 100, // remove máscara
        description: descricaoEditada,
        allowReschedule: allowRescheduleEditado,
      };

      const response = await authFetch('patient-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar edição do plano');
      }

      // Atualiza a lista localmente
      const planosAtualizados = planos.map((p) =>
        p.id === idPlanVinculado ? { ...p, ...body } : p
      );

      setPlanos(planosAtualizados);
      cancelarEdicaoPlano();
      showNotification("success", "Plano atualizado com sucesso");
    } catch (error) {
      console.error(error);
      showNotification("danger", "Erro ao salvar edição do plano");
    }
  };


  // Função para cancelar
  const cancelaAddPlano = () => {
    setMostrarAddPlano(false);
  };

  const setarTipoPlano = (e) => {
    const selectedId = e.target.value;
    setPlanoId(selectedId);

    const planoSelecionado = listPlanos.find(plano => plano.id === Number(selectedId));
    setTipoPlano(planoSelecionado);
  };

  const formatarValor = (valor) => {
    if (!valor) return '0,00';
    return Number(valor).toFixed(2).replace('.', ',');
  };

  const formatarMoeda = (valor) => {
    // Remove tudo que não for dígito
    const numero = valor.replace(/\D/g, '');

    // Converte para float com 2 casas decimais
    const float = (parseFloat(numero) / 100).toFixed(2);

    // Formata com ponto de milhar e vírgula decimal
    return float
      .replace('.', ',')                          // vírgula decimal
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');     // ponto milhar
  };

  // const formatarHora = (dataISO) => {
  //   const data = new Date(dataISO);
  //   return data.toLocaleTimeString("pt-BR", {
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     timeZone: "UTC",
  //   });
  // };

  const [controlePagamento, setControlePagamento] = useState(null);
  const [totalMensalidade, setTotalMensalidade] = useState(0); // valor alternativo  

  const handleReabrirPagamento = async (e) => {
    e.preventDefault();

    const payload = {
      patientId: Number(id),
      referenceMonth: controlePagamento.referenceMonth,
      referenceYear: controlePagamento.referenceYear,
      planSessionsHourly: controlePagamento.planSessionsHourly,
      planSessionsMonthly: controlePagamento.planSessionsMonthly,
      chargedAbsences: controlePagamento.chargedAbsences,
      nonChargedAbsences: controlePagamento.nonChargedAbsences,
      totalMonthlyPlan: controlePagamento.totalMonthlyPlan,
      totalHourlyPlan: controlePagamento.totalHourlyPlan,
      monthlyFee: controlePagamento.monthlyFee,
      totalReassessments: controlePagamento.totalReassessments,
      monthClosed: false,
      id: controlePagamento.id
    };

    const method = controlePagamento.id ? "PUT" : "POST";

    try {
      const res = await authFetch('patient-payment-control', {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao reabrir Controle");

      setMesFechadoCtrl(false);
      showNotification("success", "Controle reaberto com sucesso!");

    } catch (error) {
      console.error("Erro ao salvar Controle:", error);
      showNotification("danger", "Erro ao reabrir Controle");
    }
  }

  // const handleAtualizarPagamento = (e) => {
  //   e.preventDefault();
  // }

  const salvarControlePagamento = async (e) => {
    e.preventDefault();

    const payload = {
      patientId: Number(id),
      referenceMonth: Number(mesRef),
      referenceYear: Number(anoRef),
      planSessionsHourly: controlePagamento.paymentControlCalculated.planSessionsHourly || 0,
      planSessionsMonthly: controlePagamento.paymentControlCalculated.planSessionsMonthly || 0,
      chargedAbsences: controlePagamento.paymentControlCalculated.chargedAbsences || 0,
      nonChargedAbsences: controlePagamento.paymentControlCalculated.nonChargedAbsences || 0,
      totalMonthlyPlan: controlePagamento.paymentControlCalculated.totalMonthlyPlan || 0,
      totalHourlyPlan: controlePagamento.paymentControlCalculated.totalHourlyPlan || 0,
      monthlyFee: totalMensalidade || 0,
      totalReassessments: controlePagamento.paymentControlCalculated.totalSingle || 0,
      monthClosed: true,
      ...(controlePagamento.id && { id: controlePagamento.id }) // só adiciona se existir      
    }

    const method = controlePagamento.id ? "PUT" : "POST";

    try {
      const res = await authFetch('patient-payment-control', {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Erro ao salvar Controle");

      setMesFechadoCtrl(true);
      fetchControlePagamento();
      showNotification("success", "Controle salvo com sucesso!");

    } catch (error) {
      console.error("Erro ao salvar Controle:", error);
      showNotification("danger", "Erro ao salvar Controle");
    }
  }

  const [sessoesPorPlano, setSessoesPorPlano] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [avulsosLista, setAvulsosLista] = useState([]);

  const buscarEventosDetalhados = async () => {
    try {
      const response = await authFetch(`appointment?patientId=${id}&month=${mesRef}&year=${anoRef}&master=true`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error("Erro ao buscar eventos");

      const data = await response.json();

      // Filtrar sessões e avulsos
      const sessoes = data.filter((item) => item.appointmentType === 1);
      const avulsos = data.filter((item) => item.appointmentType === 2);

      // Agrupar sessões por plano
      const sessoesPorPlanoMap = new Map();

      sessoes.forEach((sessao) => {
        const planoId = sessao.planId;
        const nomePlano = sessao.plan?.name || "Sem Plano";

        if (sessoesPorPlanoMap.has(planoId)) {
          sessoesPorPlanoMap.get(planoId).sessoes += 1;
        } else {
          sessoesPorPlanoMap.set(planoId, {
            plano: nomePlano,
            plano_id: planoId,
            sessoes: 1,
          });
        }
      });

      // Converter map para array
      const sessoesPorPlano = Array.from(sessoesPorPlanoMap.values());

      // Atualizar estados
      setSessoesPorPlano(sessoesPorPlano);
      setSessoes(sessoes);
      setAvulsosLista(avulsos);
    } catch (error) {
      console.error("Erro ao buscar eventos:", error);
      showNotification("danger", "Erro ao buscar eventos");
    }
  };


  // Fim Funções Planos

  // Function to convert text with URLs to clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return '';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) => {
      if (part.match(urlRegex)) {
        return `<a href="${part}" target="_blank" rel="noopener noreferrer">${part}</a>`;
      }
      return part;
    }).join('');
  };

  // Function to handle curriculum save
  const handleCurriculumSave = async (e) => {
    e.preventDefault();
    setEditingCurriculum(false);
    await handleSubmitCurriculo(e);
  };

  // Function to handle observation save
  const handleObservationSave = async (e) => {
    e.preventDefault();
    setEditingObservation(false);
    await handleSubmitFormAnotacao(e);
  };

  if (loading) return <PageLoader />;
  if (error) return <p>Erro: {error}</p>;

  return (

    <div>
      <div className="content">
        <div className="card">
          <div className="card-body p-3">
            <div className="row col-md-12">
              <div className="col-auto">
                <div className="avatar rounded-circle avatar-xxl position-relative">
                  {(() => {
                    const fotoSrc = imagePacienteUrl || require('../../assets/img/perfilvazio.png');
                    const interventionDate = paciente.interventionStartDate ? new Date(paciente.interventionStartDate) : null;
                    const hoje = new Date();
                    hoje.setHours(0, 0, 0, 0);

                    let mostrarBorda = null;

                    if (interventionDate) {
                      const dataLimite = new Date(interventionDate);
                      dataLimite.setDate(dataLimite.getDate() + 30);
                      dataLimite.setHours(0, 0, 0, 0);

                      if (dataLimite >= hoje) {
                        mostrarBorda = "#00FF00"; // Verde
                      }
                    }

                    if (!mostrarBorda && paciente.statusId === 1) {
                      mostrarBorda = "#FE2E2E"; // Vermelho
                    }

                    return (
                      <img
                        src={fotoSrc}
                        alt="FotoPaciente"
                        className="personal-avatar"
                        style={
                          mostrarBorda
                            ? {
                              borderColor: mostrarBorda,
                              borderWidth: "4px",
                              borderStyle: "solid",
                            }
                            : undefined
                        }
                      />
                    );
                  })()}
                </div>
              </div>
              <div className="col-md-8">
                <h2>
                  {paciente.name}
                </h2>
                <Link to={location.state?.fromBeta ? '/PacientesBeta' : '/Pacientes'} state={location.state?.fromBeta ? { returnScrollToId: paciente.id } : undefined} className="btn btn-info btn-sm">Voltar</Link>
                <Link to={`/Pacientes/Editar/${paciente.id}`} state={{ fromBeta: location.state?.fromBeta, returnScrollToId: paciente.id }} className="btn btn-primary btn-sm mx-1">Editar</Link>
                <div id="filtroAno" className="col-md-12 row">
                  <label>Ano Referência</label>
                  <div className="col-md-2">
                    <input className="form-control form-control-sm" value={anoRef} id="anoatual" onBlur={(e) => setAnoRef(e.target.value.replace(/\D/g, ''))}
                      onChange={(e) => setAnoRef(e.target.value.replace(/\D/g, ''))} maxLength={4} />
                  </div>
                  <div className="col-md-8"></div>
                </div>
              </div>
              {
                userAPIData.userRoleId === 1 && (
                  <div className="col-md-2">
                    <button
                      className="nav-link btn btn-link p-0"
                      onClick={() => setShowConfirmDeletePaciente(true)}
                    >
                      <div className="icon icon-shape icon-sm shadow border-radius-md bg-danger text-center me-2 d-flex align-items-center justify-content-center">
                        <i className="fa-solid fa-trash-can text-white"></i>
                      </div>
                    </button>
                  </div>
                )
              }
            </div>
            <br />
            <nav>
              <div className="nav nav-tabs" id="nav-tab" role="tablist">
                <Link className="nav-link active" id="nav-dadosPaciente-tab" data-bs-toggle="tab" data-bs-target="#nav-dadosPaciente" role="tab" aria-controls="nav-dadosPaciente" aria-selected="true">Dados</Link>
                <Link
                  className="nav-link"
                  id="nav-ficha-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#nav-ficha"
                  role="tab"
                  aria-controls="nav-ficha"
                  aria-selected="false"
                  onClick={handleCurriculoClick}
                >
                  Currículo
                </Link>
                <Link
                  className="nav-link"
                  id="nav-responsaveis-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#nav-responsaveis"
                  role="tab"
                  aria-controls="nav-responsaveis"
                  aria-selected="false"
                  onClick={handleResponsaveisClick}
                >
                  Responsáveis
                </Link>
                <Link
                  className="nav-link"
                  id="nav-observacoes-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#nav-observacoes"
                  role="tab"
                  aria-controls="nav-observacoes"
                  aria-selected="false"
                  onClick={handleObservacoesClick}
                >Observações</Link>
                {userAPIData.userRoleId === 1 && (
                  <Link
                    className="nav-link"
                    id="nav-planos-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#nav-planos"
                    role="tab"
                    aria-controls="nav-planos"
                    aria-selected="false"
                    onClick={handlePlanosClick}
                  >
                    Planos
                  </Link>
                )}
                <Link className="nav-link" id="nav-anexos-tab" data-bs-toggle="tab" data-bs-target="#nav-anexos" role="tab" aria-controls="nav-anexos" aria-selected="false">Anexos</Link>
                <Link
                  className="nav-link"
                  id="nav-timeline-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#nav-timeline"
                  role="tab"
                  aria-controls="nav-timeline"
                  aria-selected="false"
                  onClick={handleTimelineClick}
                >
                  Linha do Tempo
                </Link>
                <Link className="nav-link" id="nav-datas-tab" data-bs-toggle="tab" data-bs-target="#nav-datas" role="tab" aria-controls="nav-datas" aria-selected="false">Datas de Avaliação</Link>
              </div>
            </nav>
            <div className="tab-content" id="nav-tabContent">
              <div className="tab-pane fade show active" id="nav-dadosPaciente" role="tabpanel" aria-labelledby="nav-dadosPaciente-tab">
                <div className="col-md-12 row" style={{ marginLeft: 0, marginRight: 0 }}>
                  <div className="col-md-4">
                    <label>Nome</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="nome_paciente" id="nome_paciente"
                          className="form-control form-control-sm" disabled value={paciente.name}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-user"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <label>Situação</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="status" id="status"
                          className="form-control form-control-sm" disabled value={paciente.status.name}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-clipboard-user"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <label>Modalidade</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="modalidade" id="modalidade"
                          className="form-control form-control-sm" disabled value={paciente.modality?.name}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-clipboard-user"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <label>Turno</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="turno" id="turno"
                          className="form-control form-control-sm" disabled value={turnoLabels[Number(paciente.shift)] ?? 'Não informado'}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-regular fa-clock"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2"></div>
                  <div className="col-md-4">
                    <label>Nome do Pai</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="nomePai" id="nomePai"
                          className="form-control form-control-sm" disabled value={paciente.fatherName}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-user-tie"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label>Nome do Mãe</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="nomeMae" id="nomeMae"
                          className="form-control form-control-sm" disabled value={paciente.motherName}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-person-breastfeeding"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label>Escolaridade</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="nomeMae" id="nomeMae"
                          className="form-control form-control-sm" disabled value={paciente.educationLevel}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-graduation-cap"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data de Nascimento</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="dataNascimento"
                          id="dataNascimento"
                          className="form-control form-control-sm"
                          disabled
                          value={paciente.birthdate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Idade</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <input type="text" name="Idade" id="Idade"
                          className="form-control form-control-sm" disabled value={idadeFormatada}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data Anamnese</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="dataAnamnese"
                          id="dataAnamnese"
                          className="form-control form-control-sm"
                          disabled
                          value={paciente.anamnesisDate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data Fim do Contrato</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="dataFimContrato"
                          id="dataFimContrato"
                          className="form-control form-control-sm"
                          disabled
                          value={paciente.contractEndDate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <label>Parceria</label>
                    <div className="col-md-12 input-group input-icone">
                      {planos.map((plano) => (
                        <div className="input text" key={plano.id}>
                          <input
                            type="text"
                            name="nome_plano"
                            className="form-control form-control-sm"
                            id="nome-plano"
                            disabled
                            value={plano.plan.name}
                          />
                        </div>
                      ))}
                      <span className="input-group-text">
                        <i className="fa-solid fa-file-invoice"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="tab-pane fade" id="nav-ficha" role="tabpanel" aria-labelledby="nav-ficha-tab">
                <div className="col-md-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <div className="text">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <label className="mb-2">Currículo</label>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-info mt-2"
                        onClick={() => setEditingCurriculum(!editingCurriculum)}
                      >
                        {editingCurriculum ? 'Cancelar' : 'Editar'}
                      </button>
                    </div>
                    <form onSubmit={handleCurriculumSave}>
                      <blockquote>
                        <div id="container_ficha" className="container_ficha">
                          {editingCurriculum ? (
                            <>
                              <textarea
                                className="form-control form-control-sm"
                                style={{
                                  minHeight: '400px',
                                  padding: '12px',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap'
                                }}
                                value={descricaoCurriculo || ''}
                                onChange={(e) => setDescricaoCurriculo(e.target.value)}
                                placeholder="Digite o currículo do paciente. URLs serão automaticamente convertidas em links clicáveis."
                              />
                              <div className="d-flex justify-content-between align-items-center mt-2">
                                <span id="caracteres-ficha">
                                  {(descricaoCurriculo?.length || 0)} caracteres
                                </span>
                                <button type="submit" className="btn btn-primary btn-sm">
                                  Salvar
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                className="form-control form-control-sm"
                                style={{
                                  minHeight: '400px',
                                  padding: '12px',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: descricaoCurriculo ? renderTextWithLinks(descricaoCurriculo) : ''
                                }}
                              />
                              <div className="mt-2">
                                <span id="caracteres-ficha">
                                  {(descricaoCurriculo?.length || 0)} caracteres
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </blockquote>
                    </form>
                  </div>
                </div>
              </div>
              <div className="tab-pane fade" id="nav-responsaveis" role="tabpanel" aria-labelledby="nav-responsaveis-tab">
                <div className="col-md-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Responsáveis</h6>
                    {!showFormResponsaveis && !visualizandoResponsavel && !editandoResponsavel && (
                      <button
                        onClick={() => {
                          setShowFormResponsaveis(true);
                          setEditandoResponsavel(null);
                          setVisualizandoResponsavel(null);
                        }}
                        className="btn btn-primary btn-sm"
                      >
                        <i className="fa-solid fa-plus me-1"></i>
                        Adicionar Responsável
                      </button>
                    )}
                  </div>
                  
                  {loadingResponsaveis ? (
                    <TableLoader />
                  ) : responsaveis.length > 0 ? (
                    <div className="mb-4">
                      <style>{`
                        .table-responsive table tbody tr:hover {
                          background-color: transparent !important;
                        }
                      `}</style>
                      <div className="table-responsive">
                        <table className="table align-items-center mb-0" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ padding: '0.5rem', width: '60px' }}></th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ padding: '0.5rem' }}>Nome</th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ padding: '0.5rem' }}>Parentesco</th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ padding: '0.5rem' }}>E-mail</th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ padding: '0.5rem' }}>Telefone</th>
                              <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style={{ textAlign: 'center', padding: '0.5rem' }}>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {responsaveis.map((r, index) => (
                              <tr key={index}>
                                <td style={{ padding: '0.4rem 0.5rem', width: '60px' }}>
                                  <img
                                    src={r.imageUrl || require('../../assets/img/perfilvazio.png')}
                                    alt={r.guardian.name || ''}
                                    onClick={() => r.imageUrl && setFotoResponsavelModal(r)}
                                    style={{
                                      width: 44,
                                      height: 44,
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      display: 'block',
                                      cursor: r.imageUrl ? 'pointer' : 'default',
                                      opacity: r.imageUrl ? 1 : 0.45,
                                    }}
                                    title={r.imageUrl ? 'Ver foto' : 'Sem foto cadastrada'}
                                  />
                                </td>
                                <td className="text-xs mb-0" style={{ padding: '0.5rem' }}>{r.guardian.name || "-"}</td>
                                <td className="text-xs mb-0" style={{ padding: '0.5rem' }}>
                                  <span className="badge bg-info">{r.guardianType || "-"}</span>
                                </td>
                                <td className="text-xs mb-0" style={{ padding: '0.5rem' }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <span>{r.guardian.email || "-"}</span>
                                    {r.guardian.email && (
                                      r.hasLogin
                                        ? <span className="badge bg-success" title="Responsável criou login no aplicativo">
                                            <i className="bi bi-check-circle me-1"></i>Login ativo
                                          </span>
                                        : <span className="badge bg-secondary" title="Responsável ainda não criou login no aplicativo">
                                            <i className="bi bi-clock me-1"></i>Aguardando registro
                                          </span>
                                    )}
                                  </div>
                                </td>
                                <td className="text-xs mb-0" style={{ padding: '0.5rem' }}>
                                  {r.guardian.cellphone || r.guardian.phone || "-"}
                                </td>
                                <td className="text-xs font-weight-bold mb-0" style={{ textAlign: 'center', padding: '0.5rem' }}>
                                  <button
                                    onClick={() => handleVisualizarResponsavel(r)}
                                    title="Visualizar"
                                    className="btn btn-link p-0 me-3"
                                    style={{ border: 'none', background: 'none' }}
                                  >
                                    <i className="bi bi-eye text-info"></i>
                                  </button>
                                  <button
                                    onClick={() => handleEditarResponsavel(r)}
                                    title="Editar"
                                    className="btn btn-link p-0 me-3"
                                    style={{ border: 'none', background: 'none' }}
                                  >
                                    <i className="bi bi-pencil-square text-primary"></i>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      confirmarExclusaoResponsavel(r);
                                    }}
                                    title="Excluir"
                                    className="btn btn-link p-0 me-3"
                                    style={{ border: 'none', background: 'none' }}
                                  >
                                    <i className="bi bi-trash text-danger"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                  {showFormResponsaveis && (
                    <form className="py-3" onSubmit={handleAddResponsavel}>
                      <h6 className="mb-3">Cadastrar Novo Responsável</h6>
                      <div className="row">
                        <div className="col-md-6 mb-2">
                          <label>Nome *</label>
                          <CustomAutoComplete
                            placeholder="Digite o nome do responsável"
                            value={novoResponsavel.name}
                            setValue={(value) => {
                              setNovoResponsavel((prev) => ({ ...prev, name: value }));
                            }}
                            onSearch={buscarResponsaveis}
                            onSelect={handleSelectResponsavel}
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Parentesco *</label>
                          <input
                            type="text"
                            name="guardianType"
                            className="form-control form-control-sm"
                            value={novoResponsavel.guardianType}
                            onChange={handleInputChangeResp}
                            placeholder="Ex: Pai, Mãe, Avô, etc."
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>E-mail</label>
                          <input
                            type="email"
                            name="email"
                            className="form-control form-control-sm"
                            value={novoResponsavel.email}
                            onChange={handleInputChangeResp}
                            placeholder="email@exemplo.com"
                          />
                          <small className="text-muted d-flex align-items-center gap-1 mt-1">
                            <i className="bi bi-info-circle text-info"></i>
                            O e-mail será usado para permitir acesso ao aplicativo mobile. Caso não queira permitir acesso, não cadastre um e-mail.
                          </small>
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>CPF</label>
                          <input
                            type="text"
                            name="cpf"
                            className="form-control form-control-sm"
                            value={novoResponsavel.cpf}
                            onChange={handleInputChangeResp}
                            placeholder="000.000.000-00"
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Telefone</label>
                          <input
                            type="text"
                            name="phone"
                            className="form-control form-control-sm"
                            value={novoResponsavel.phone}
                            onChange={handleInputChangeResp}
                            placeholder="(00) 0000-0000"
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Celular</label>
                          <input
                            type="text"
                            name="cellphone"
                            className="form-control form-control-sm"
                            value={novoResponsavel.cellphone}
                            onChange={handleInputChangeResp}
                            placeholder="(00) 00000-0000"
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>CEP</label>
                          <input
                            type="text"
                            name="zipCode"
                            className="form-control form-control-sm"
                            value={novoResponsavel.zipCode}
                            onChange={handleInputChangeResp}
                            onBlur={handleZipCodeBlur}
                            placeholder="00000-000"
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>Endereço</label>
                          <input
                            type="text"
                            name="address"
                            className="form-control form-control-sm"
                            value={novoResponsavel.address}
                            onChange={handleInputChangeResp}
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>Número</label>
                          <input
                            type="text"
                            name="addressNumber"
                            className="form-control form-control-sm"
                            value={novoResponsavel.addressNumber}
                            onChange={handleInputChangeResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Complemento</label>
                          <input
                            type="text"
                            name="addressComplement"
                            className="form-control form-control-sm"
                            value={novoResponsavel.addressComplement}
                            onChange={handleInputChangeResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Bairro</label>
                          <input
                            type="text"
                            name="neighborhood"
                            className="form-control form-control-sm"
                            value={novoResponsavel.neighborhood}
                            onChange={handleInputChangeResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Cidade</label>
                          <input
                            type="text"
                            name="city"
                            className="form-control form-control-sm"
                            value={novoResponsavel.city}
                            onChange={handleInputChangeResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Estado</label>
                          <input
                            type="text"
                            name="state"
                            className="form-control form-control-sm"
                            value={novoResponsavel.state}
                            onChange={handleInputChangeResp}
                            maxLength={2}
                            placeholder="UF"
                          />
                        </div>
                        <div className="col-md-12 mt-3">
                          <button
                            type="button"
                            className="btn btn-info btn-sm me-2"
                            onClick={() => {
                              setShowFormResponsaveis(false);
                              setNovoResponsavel({
                                guardianId: "",
                                guardianType: "",
                                patientId: paciente.id || "",
                                name: '',
                                email: '',
                                cpf: '',
                                zipCode: '',
                                address: '',
                                neighborhood: '',
                                addressNumber: '',
                                addressComplement: '',
                                state: '',
                                city: '',
                                phone: '',
                                cellphone: ''
                              });
                            }}
                          >
                            Cancelar
                          </button>
                          <button type="submit" className="btn btn-primary btn-sm">
                            Salvar e Vincular
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {/* Visualização de Responsável */}
                  {visualizandoResponsavel && (
                    <div className="py-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Visualizar Responsável</h6>
                        <button
                          onClick={handleFecharVisualizarEditar}
                          className="btn btn-sm btn-secondary"
                        >
                          Fechar
                        </button>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-2">
                          <label>Nome</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.name || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>CPF</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.cpf ? applyCpfMask(String(visualizandoResponsavel.cpf).replace(/\D/g, '')) : ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>E-mail</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.email || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Parentesco</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.guardianType || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Telefone</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.phone ? applyPhoneMask(String(visualizandoResponsavel.phone).replace(/\D/g, '')) : ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Celular</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.cellphone ? applyPhoneMask(String(visualizandoResponsavel.cellphone).replace(/\D/g, '')) : ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>CEP</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.zipCode ? applyZipCodeMask(String(visualizandoResponsavel.zipCode).replace(/\D/g, '')) : ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>Endereço</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.address || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>Número</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.addressNumber || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Complemento</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.addressComplement || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Bairro</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.neighborhood || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Cidade</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.city || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Estado</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={visualizandoResponsavel.state || ""}
                            readOnly
                          />
                        </div>
                        <div className="col-md-12 mt-3">
                          <button
                            onClick={() => handleEditarResponsavel({
                              guardian: { id: visualizandoResponsavel.id },
                              guardianType: visualizandoResponsavel.guardianType,
                              id: visualizandoResponsavel.patientGuardianId
                            })}
                            className="btn btn-primary btn-sm me-2"
                          >
                            Editar
                          </button>
                          <button
                            onClick={handleFecharVisualizarEditar}
                            className="btn btn-secondary btn-sm"
                          >
                            Voltar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Formulário de Edição de Responsável */}
                  {editandoResponsavel && (
                    <form className="py-3" onSubmit={handleEditResponsavel}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Editar Responsável</h6>
                        <button
                          type="button"
                          onClick={handleFecharVisualizarEditar}
                          className="btn btn-sm btn-secondary"
                        >
                          Cancelar
                        </button>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-2">
                          <label>Nome *</label>
                          <input
                            type="text"
                            name="name"
                            className="form-control form-control-sm"
                            value={editResponsavel.name}
                            onChange={handleInputChangeEditResp}
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Parentesco *</label>
                          <input
                            type="text"
                            name="guardianType"
                            className="form-control form-control-sm"
                            value={editResponsavel.guardianType}
                            onChange={handleInputChangeEditResp}
                            placeholder="Ex: Pai, Mãe, Avô, etc."
                            required
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>E-mail</label>
                          <input
                            type="email"
                            name="email"
                            className="form-control form-control-sm"
                            value={editResponsavel.email}
                            onChange={handleInputChangeEditResp}
                            placeholder="email@exemplo.com"
                          />
                          <small className="text-muted d-flex align-items-center gap-1 mt-1">
                            <i className="bi bi-info-circle text-info"></i>
                            O e-mail será usado para permitir acesso ao aplicativo mobile. Caso não queira permitir acesso, não cadastre um e-mail.
                          </small>
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>CPF</label>
                          <input
                            type="text"
                            name="cpf"
                            className="form-control form-control-sm"
                            value={editResponsavel.cpf}
                            onChange={handleInputChangeEditResp}
                            placeholder="000.000.000-00"
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Telefone</label>
                          <input
                            type="text"
                            name="phone"
                            className="form-control form-control-sm"
                            value={editResponsavel.phone}
                            onChange={handleInputChangeEditResp}
                            placeholder="(00) 0000-0000"
                          />
                        </div>
                        <div className="col-md-6 mb-2">
                          <label>Celular</label>
                          <input
                            type="text"
                            name="cellphone"
                            className="form-control form-control-sm"
                            value={editResponsavel.cellphone}
                            onChange={handleInputChangeEditResp}
                            placeholder="(00) 00000-0000"
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>CEP</label>
                          <input
                            type="text"
                            name="zipCode"
                            className="form-control form-control-sm"
                            value={editResponsavel.zipCode}
                            onChange={handleInputChangeEditResp}
                            onBlur={handleZipCodeBlurEdit}
                            placeholder="00000-000"
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>Endereço</label>
                          <input
                            type="text"
                            name="address"
                            className="form-control form-control-sm"
                            value={editResponsavel.address}
                            onChange={handleInputChangeEditResp}
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label>Número</label>
                          <input
                            type="text"
                            name="addressNumber"
                            className="form-control form-control-sm"
                            value={editResponsavel.addressNumber}
                            onChange={handleInputChangeEditResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Complemento</label>
                          <input
                            type="text"
                            name="addressComplement"
                            className="form-control form-control-sm"
                            value={editResponsavel.addressComplement}
                            onChange={handleInputChangeEditResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Bairro</label>
                          <input
                            type="text"
                            name="neighborhood"
                            className="form-control form-control-sm"
                            value={editResponsavel.neighborhood}
                            onChange={handleInputChangeEditResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Cidade</label>
                          <input
                            type="text"
                            name="city"
                            className="form-control form-control-sm"
                            value={editResponsavel.city}
                            onChange={handleInputChangeEditResp}
                          />
                        </div>
                        <div className="col-md-3 mb-2">
                          <label>Estado</label>
                          <input
                            type="text"
                            name="state"
                            className="form-control form-control-sm"
                            value={editResponsavel.state}
                            onChange={handleInputChangeEditResp}
                          />
                        </div>
                        <div className="col-md-12 mt-3">
                          <button type="submit" className="btn btn-primary btn-sm me-2">
                            Salvar
                          </button>
                          <button
                            type="button"
                            onClick={handleFecharVisualizarEditar}
                            className="btn btn-secondary btn-sm"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
              <div
                className="tab-pane fade"
                id="nav-observacoes"
                role="tabpanel"
                aria-labelledby="nav-observacoes-tab"
              >
                <div className="col-md-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <div className="text">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <label className="mb-2">Campo para anotações</label>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-info mt-2"
                        onClick={() => setEditingObservation(!editingObservation)}
                      >
                        {editingObservation ? 'Cancelar' : 'Editar'}
                      </button>
                    </div>
                    <blockquote>
                      <form onSubmit={handleObservationSave}>
                        <div id="container_anotacao" className="container_anotacao">
                          {editingObservation ? (
                            <>
                              <textarea
                                className="form-control form-control-sm"
                                style={{
                                  minHeight: '400px',
                                  padding: '12px',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap'
                                }}
                                value={descricaoAnotacao || ''}
                                onChange={(e) => setDescricaoAnotacao(e.target.value)}
                                placeholder="Digite as observações do paciente. URLs serão automaticamente convertidas em links clicáveis."
                                maxLength={16000}
                              />
                              <div className="d-flex justify-content-between align-items-center mt-2">
                                <span id="caracteres-anotacao">
                                  {descricaoAnotacao?.length} / 16000 caracteres
                                </span>
                                <button type="submit" className="btn btn-primary btn-sm">
                                  Salvar
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                className="form-control form-control-sm"
                                style={{
                                  minHeight: '400px',
                                  padding: '12px',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: descricaoAnotacao ? renderTextWithLinks(descricaoAnotacao) : ''
                                }}
                              />
                              <div className="mt-2">
                                <span id="caracteres-anotacao">
                                  {descricaoAnotacao?.length} / 16000 caracteres
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </form>
                    </blockquote>
                  </div>
                </div>
              </div>
              {userAPIData.userRoleId === 1 && (
                <div className="tab-pane fade" id="nav-planos" role="tabpanel" aria-labelledby="nav-planos-tab">
                  <div className="col-md-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <div>
                      <div className="col-md-12 row py-2" style={{ marginLeft: 0, marginRight: 0 }}>
                        <h6>Contagem de Atendimentos</h6>
                        <div className="col-md-1">
                          <label>Ano Referência</label>
                        </div>
                        <div className="col-md-2">
                          <div className="input-group mb-3">
                            <input type="text" name="anopagamento" id="anopagamento" className="form-control form-control-sm ano"
                              value={anoRef} onChange={(e) => setAnoRef(e.target.value.replace(/\D/g, ''))} maxLength={4}
                              style={{ marginLeft: "1em" }} />
                            <span className="input-group-text"><i className="fa-solid fa-calendar-plus"></i></span>
                          </div>
                        </div>
                        <div className="col-md-1">
                          <label>Mês Referência</label>
                        </div>
                        <div className="col-md-2">
                          <div className="input-group mb-3">
                            <select
                              name="mespagamento"
                              id="mespagamento"
                              className="form-control form-control-sm"
                              value={mesRef}
                              onChange={(e) => setMesRef(e.target.value)}
                              style={{ marginLeft: "1em" }}
                            >
                              {meses.map((mes) => (
                                <option key={mes} value={mes}>
                                  {mes}
                                </option>
                              ))}
                            </select>
                            <span className="input-group-text">
                              <i className="fa-solid fa-calendar-plus"></i>
                            </span>
                          </div>
                        </div>

                        <div className="col-md-2">
                          {/* <button type="submit" form="form-filtrarpagamento" className="btn btn-info btn-sm">Filtrar</button> */}
                        </div>
                        <div className="col-md-2">
                          <button form="form-salvarpagamento" className="btn btn-info btn-sm" onClick={MostrarDetalhesPagMes}>Detalhes</button>
                        </div>
                        <div className="form-group col-md-2">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="salvo"
                              checked={mesFechadoCtrl ? true : false} // Quando controlePagamento existir, o checkbox ficará marcado
                              disabled
                            />
                            <label className="custom-control-label" htmlFor="salvo">
                              Salvo
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12 row" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="form-group col-md-3">
                        <label>Sessões Planos Horas</label>
                        <div className="input-group">
                          <input
                            type="text"
                            name="total_sessoes_horas"
                            id="total_sessoes_horas"
                            className="form-control form-control-sm"
                            readOnly
                            style={{ marginLeft: "1em" }}
                            value={
                              mesFechadoCtrl === true
                                ? controlePagamento?.planSessionsHourly ?? 0
                                : controlePagamento?.paymentControlCalculated?.planSessionsHourly ?? 0
                            }
                          />
                          <span className="input-group-text"><i className="fa-solid fa-calculator"></i></span>
                        </div>
                      </div>
                      <div className="form-group col-md-3">
                        <label>Sessões Planos Mensais</label>
                        <div className="input-group">
                          <input type="text" name="total_sessoes_mensais" id="total_sessoes_mensais" className="form-control form-control-sm" readOnly style={{ marginLeft: "1em" }}
                            value={
                              mesFechadoCtrl === true
                                ? controlePagamento?.planSessionsMonthly ?? 0
                                : controlePagamento?.paymentControlCalculated?.planSessionsMonthly ?? 0
                            }
                          />
                          <span className="input-group-text"><i className="fa-solid fa-calculator"></i></span>
                        </div>
                      </div>
                      <div className="form-group col-md-3">
                        <label>Faltas Faturadas</label>
                        <div className="input-group">
                          <input type="text" name="faltas_faturadas" id="faltas_faturadas" className="form-control form-control-sm" readOnly style={{ marginLeft: "1em" }}
                            value={
                              mesFechadoCtrl === true
                                ? controlePagamento?.chargedAbsence ?? 0
                                : controlePagamento?.paymentControlCalculated?.chargedAbsence ?? 0
                            }
                          />
                          <span className="input-group-text"><i className="fa-solid fa-calculator"></i></span>
                        </div>
                      </div>
                      <div className="form-group col-md-3">
                        <label>Faltas Não Faturadas</label>
                        <div className="input-group">
                          <input type="text" name="faltas_naofaturadas" id="faltas_naofaturadas" className="form-control form-control-sm" readOnly style={{ marginLeft: "1em" }}
                            value={
                              mesFechadoCtrl === true
                                ? controlePagamento?.nonChargedAbsence ?? 0
                                : controlePagamento?.paymentControlCalculated?.nonChargedAbsence ?? 0
                            }
                          />
                          <span className="input-group-text"><i className="fa-solid fa-calculator"></i></span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12 row" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="form-group col-md-3">
                        <label>Total Planos Mensais</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={
                              mesFechadoCtrl === true
                                ? formatarValor(controlePagamento?.totalMonthlyPlan ?? 0)
                                : formatarValor(controlePagamento?.paymentControlCalculated?.totalMonthlyPlan ?? 0)
                            }
                            readOnly
                            style={{ marginLeft: '1em' }}
                          />
                          <span className="input-group-text">
                            <i className="fa-solid fa-brazilian-real-sign"></i>
                          </span>
                        </div>
                      </div>
                      <div className="form-group col-md-3">
                        <label>Total Planos por Hora</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={
                              mesFechadoCtrl === true
                                ? formatarValor(controlePagamento?.totalHourlyPlan ?? 0)
                                : formatarValor(controlePagamento?.paymentControlCalculated?.totalHourlyPlan ?? 0)
                            }
                            readOnly
                            style={{ marginLeft: '1em' }}
                          />
                          <span className="input-group-text"><i className="fa-solid fa-brazilian-real-sign"></i></span>
                        </div>
                      </div>
                      <div className="form-group col-md-3">
                        <label>Avulsos</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={
                              mesFechadoCtrl === true
                                ? formatarValor(controlePagamento?.totalReassessments ?? 0)
                                : formatarValor(controlePagamento?.paymentControlCalculated?.totalSingle ?? 0)
                            }
                            readOnly
                            style={{ marginLeft: '1em' }}
                          />
                          <span className="input-group-text"><i className="fa-solid fa-brazilian-real-sign"></i></span>
                        </div>
                      </div>
                      <div className="form-group col-md-3">
                        <label>Total Mensalidade</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={
                              mesFechadoCtrl === true
                                ? formatarValor(controlePagamento?.monthlyFee ?? 0)
                                : formatarValor(totalMensalidade)
                            }
                            readOnly
                            style={{ marginLeft: '1em' }}
                          />
                          <span className="input-group-text">
                            <i className="fa-solid fa-brazilian-real-sign"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 row mt-2" style={{ marginLeft: 0, marginRight: 0 }}>
                        {mesFechadoCtrl === true ? (
                          <>
                            <div className="col-md-8"></div>
                            <div className="col-md-2">
                              {/* <button
                                form="form-salvarpagamento"
                                className="btn btn-primary btn-sm"
                                style={{ marginLeft: '1em' }}
                                onClick={handleAtualizarPagamento}
                              >
                                Atualizar
                              </button> */}
                            </div>
                            <div className="col-md-2">
                              <button
                                className="btn btn-danger btn-sm"
                                style={{ marginLeft: '1em' }}
                                onClick={handleReabrirPagamento}
                              >
                                Reabrir
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="col-md-10"></div>
                            <div className="col-md-2">
                              <button form="form-salvarpagamento" className="btn btn-primary btn-sm" onClick={salvarControlePagamento}>
                                Salvar Mês
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <hr />
                  <div className="col-md-12 row centralizar" style={{ marginLeft: 0, marginRight: 0 }}>
                    <h6>Planos Vinculados</h6>
                    {planos &&
                      planos.map((p) => (
                        <div className="col-md-6 row" key={p.id}>
                          <div className="col-md-12 linha-vertical">
                            <label>Plano</label>
                            <div className="form-group">
                              <div className="col-md-8 input-group">
                                <input type="text" name="nome_plano" id="nome_plano" className="form-control form-control-sm" readOnly style={{ marginLeft: "1em" }}
                                  value={p.plan.name} />
                                <span className="input-group-text"><i className="fa-solid fa-clipboard-list"></i></span>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12">
                          </div>
                          <div className="col-md-6 linha-vertical-left">
                            <label>Tipo do Plano</label>
                            <div className="form-group">
                              <div className="col-md-8">
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    disabled
                                    style={{ marginLeft: '1em' }}
                                    value={
                                      p.plan.planTypeId === 0
                                        ? 'Mensal'
                                        : p.plan.planTypeId === 1
                                          ? 'Sessão'
                                          : 'Sem Tipo'
                                    }
                                  />
                                  <span className="input-group-text">
                                    <i className="fa-solid fa-calculator"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 linha-vertical-right">
                            <label>Valor Acordado</label>
                            <div className="form-group">
                              <div className="col-md-8 input-group">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  disabled={editandoPlanoId !== p.id}
                                  style={{ marginLeft: '1em' }}
                                  value={editandoPlanoId === p.id ? valorEditado : formatarValor(p.contractValue)}
                                  onChange={(e) => setValorEditado(formatarMoeda(e.target.value))}
                                />
                                <span className="input-group-text"><i className="fa-solid fa-brazilian-real-sign"></i></span>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12 linha-vertical">
                            <label>Descrição do Plano</label>
                            <textarea
                              className="form-control form-control-sm"
                              disabled={editandoPlanoId !== p.id}
                              value={editandoPlanoId === p.id ? descricaoEditada : (p.description ?? '')}
                              onChange={(e) => setDescricaoEditada(e.target.value)}
                            />
                          </div>
                          <div className="col-md-12 linha-vertical" style={{ marginTop: '0.5em' }}>
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`allowReschedule_${p.id}`}
                                checked={editandoPlanoId === p.id ? allowRescheduleEditado : (p.allowReschedule ?? false)}
                                disabled={editandoPlanoId !== p.id}
                                onChange={(e) => setAllowRescheduleEditado(e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`allowReschedule_${p.id}`}>
                                Permite reposição
                              </label>
                            </div>
                          </div>
                          <div className="col-md-12 linha-vertical">
                            <div className="col-md-12 row">
                              {editandoPlanoId === p.id ? (
                                <>
                                  <div className="col-md-6">
                                    <br />
                                    <button className="btn btn-sm btn-info me-2" onClick={cancelarEdicaoPlano}>
                                      Cancelar
                                    </button>
                                  </div>
                                  <div className="col-md-6">
                                    <br />
                                    <button className="btn btn-sm btn-primary" onClick={() => salvarEdicaoPlano(p.planId, p.id)}>
                                      Salvar
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="col-md-6">
                                    <br />
                                    <button className="btn btn-sm btn-danger me-2" onClick={() => handleExcluirPlano(p.id)}>
                                      Excluir Plano
                                    </button>
                                  </div>
                                  <div className="col-md-6">
                                    <br />
                                    <button className="btn btn-sm btn-info" onClick={() => iniciarEdicaoPlano(p)}>
                                      Editar
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <hr />
                        </div>
                      ))}

                    <div className="col-md-6">
                      {mostrarAddPlano && (
                        <div className="col-md-12" id="AddPlano">
                          <form onSubmit={handleSubmitPlanos}>
                            <div className="row">
                              <div className="col-md-6">
                                <label>Plano</label>
                                <div className="form-group">
                                  <div className="col-md-8 input-group">
                                    <select
                                      className="form-control form-control-sm"
                                      value={planoId}
                                      onChange={setarTipoPlano}
                                      style={{ marginLeft: '1em' }}
                                    >
                                      {listPlanos.map((plano) => (
                                        <option key={plano.id} value={plano.id}>
                                          {plano.name}
                                        </option>
                                      ))}
                                    </select>
                                    <span className="input-group-text">
                                      <i className="fa-solid fa-clipboard-list"></i>
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-6"></div>

                              <div className="col-md-6">
                                <label>Tipo do Plano</label>
                                <div className="form-group">
                                  <div className="col-md-8 input-group">
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      readOnly
                                      style={{ marginLeft: '1em' }}
                                      id="Tp_Plano"
                                      value={
                                        tipoPlano.planTypeId === 0
                                          ? 'Mensal'
                                          : tipoPlano.planTypeId === 1
                                            ? 'Sessão'
                                            : 'Sem Tipo'
                                      }
                                    />
                                    <span className="input-group-text">
                                      <i className="fa-solid fa-calculator"></i>
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <label>Valor Acordado</label>
                                <div className="form-group">
                                  <div className="col-md-8 input-group">
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      id="valor_acordado"
                                      style={{ marginLeft: '1em' }}
                                      value={valorAcordado}
                                      onChange={(e) => setValorAcordado(formatarMoeda(e.target.value))}
                                    />
                                    <span className="input-group-text">
                                      <i className="fa-solid fa-brazilian-real-sign"></i>
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-12">
                                <label>Descrição do Plano</label>
                                <div className="col-md-12">
                                  <textarea
                                    className="form-control form-control-sm"
                                    value={descricaoPlano}
                                    onChange={(e) => setDescricaoPlano(e.target.value)}
                                  />
                                </div>
                              </div>

                              <div className="col-md-12" style={{ marginTop: '0.5em' }}>
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="allowReschedulePlano"
                                    checked={allowReschedulePlano}
                                    onChange={(e) => setAllowReschedulePlano(e.target.checked)}
                                  />
                                  <label className="form-check-label" htmlFor="allowReschedulePlano">
                                    Permite reposição
                                  </label>
                                </div>
                              </div>

                              <div className="col-md-6 row">
                                <div className="col-md-6">
                                  <br />
                                  <button
                                    type="button"
                                    className="btn btn-info btn-sm"
                                    onClick={cancelaAddPlano}
                                  >
                                    Cancelar
                                  </button>
                                </div>
                                <div className="col-md-6">
                                  <br />
                                  <button type="submit" className="btn btn-primary btn-sm">
                                    Vincular
                                  </button>
                                </div>
                              </div>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                    <br />
                    <div className="col-md-12">
                      <button onClick={(e) => setMostrarAddPlano(true)} className="btn btn-primary btn-sm" id="btnaddplano">Vincular Plano</button>
                    </div>
                  </div>
                </div>
              )}
              <div className="tab-pane fade" id="nav-anexos" role="tabpanel" aria-labelledby="nav-anexos-tab">
                <div className="col-md-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <div className="">
                    <label>Anexe aqui os links dos arquivos</label>
                    <form onSubmit={handleSubmitFormAnotacao}>
                      <div id="container_anotacao" className="container_anotacao">
                        <div
                          ref={observacaoRef}
                          className="form-control form-control-sm"
                          style={{
                            minHeight: '400px',
                            padding: '12px',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap'
                          }}
                          contentEditable="true"
                          dangerouslySetInnerHTML={{
                            __html: descricaoAnotacao ? convertLinksToHtml(descricaoAnotacao) : ''
                          }}
                          onInput={(e) => {
                            const text = e.target.innerText || '';
                            setDescricaoAnotacao(text);
                          }}
                          onBlur={(e) => {
                            const text = e.target.innerText || '';
                            e.target.innerHTML = convertLinksToHtml(text);
                          }}
                          placeholder="Cole aqui os links dos arquivos. URLs serão automaticamente convertidas em links clicáveis."
                        />
                      </div>
                      <span id="caracteres-anotacoes">
                        {descricaoAnotacao?.length} / 16000 caracteres
                      </span>
                      <div className="mt-2">
                        <button type="submit" className="btn btn-primary btn-sm">
                          Salvar
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              <div className="tab-pane fade" id="nav-timeline" role="tabpanel" aria-labelledby="nav-timeline-tab">
                <br />
                <h6>Novo Histórico</h6>
                <hr />
                <form onSubmit={handleSubmitLinhaDoTempo}>
                  <div className="col-md-12 row" style={{ marginLeft: 0, marginRight: 0 }}>
                    <div className="col-md-2 form-group">
                      <input type="date" name="date" id="data_acontecimento" className="form-control form-control-sm"
                        value={novaLinhadotempo.date}
                        onChange={handleInputChangeLT} />
                    </div>
                    <div className="col-md-8 form-group">
                      <textarea rows={3} className="form-control form-control-sm" id="descritivo" name="description" maxLength={16000}
                        value={novaLinhadotempo.description}
                        onChange={handleInputChangeLT} />
                      <span id="caracteres-descritivo"></span>
                    </div>
                    <div className="col-md-2">
                      <button type="submit" className="btn btn-primary btn-sm">Salvar</button>
                    </div>
                  </div>
                </form>
                <br />
                <div className="">
                  <h6>Histórico de Acontecimentos</h6>
                  <hr />
                  {linhadotempo &&
                    linhadotempo.map((lt) => (
                      <div className="col-md-12 row mb-2" key={lt.id} style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className="col-md-2 form-group">
                          <input
                            type="date"
                            name="date"
                            className="form-control form-control-sm"
                            readOnly={editandoLTId !== lt.id}
                            value={editandoLTId === lt.id ? ltEditando.date : lt.date}
                            onChange={editandoLTId === lt.id ? handleChangeEdicao : undefined}
                          />
                        </div>
                        <div className="col-md-8 form-group">
                          <textarea
                            name="description"
                            className="form-control form-control-sm"
                            rows={3}
                            readOnly={editandoLTId !== lt.id}
                            value={editandoLTId === lt.id ? ltEditando.description : lt.description}
                            onChange={editandoLTId === lt.id ? handleChangeEdicao : undefined}
                          />
                        </div>
                        <div className="col-md-2 form-group d-flex gap-1">
                          {editandoLTId === lt.id ? (
                            <>
                              <button className="btn btn-primary btn-sm" onClick={() => salvarEdicaoLT(lt.id)}>Salvar</button>
                              <button className="btn btn-danger btn-sm" onClick={cancelarEdicao}>Cancelar</button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-info btn-sm" onClick={() => iniciarEdicao(lt)}>Editar</button>
                              <button className="btn btn-danger btn-sm" onClick={() => excluirLinhaDoTempo(lt)}>Excluir</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              <div className="tab-pane fade" id="nav-datas" role="tabpanel" aria-labelledby="nav-datas-tab">
                <div className="col-md-12 row" style={{ marginLeft: 0, marginRight: 0 }}>
                  <div className="col-md-3">
                    <label>Data Ultima Avaliação</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="data_avaliacao"
                          id="data_avaliacao"
                          className="form-control form-control-sm"
                          value={paciente.assessmentDate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data Próxima Avaliação</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="data_prox_avaliacao"
                          id="data_prox_avaliacao"
                          className="form-control form-control-sm"
                          value={paciente.nextAssessmentDate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data Início Intervenção</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="interventionStartDate"
                          id="interventionStartDate"
                          className="form-control form-control-sm"
                          value={paciente.interventionStartDate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data Anamnese</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="anamnesisDate"
                          id="anamnesisDate"
                          className="form-control form-control-sm"
                          disabled
                          value={paciente.anamnesisDate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data de Nascimento</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="birthdate"
                          id="birthdate"
                          className="form-control form-control-sm"
                          value={paciente.birthdate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>Data Fim do Contrato</label>
                    <div className="form-group">
                      <div className="col-md-12 input-group input-icone">
                        <DateDisplay
                          name="contractEndDate"
                          id="contractEndDate"
                          className="form-control form-control-sm"
                          value={paciente.contractEndDate}
                          style={{ width: "100%", borderRight: "1px solid #d2d6da", borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                        <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* -----------------MODAL DETALHES PLANOS NO MÊS-------------------------------- */}
      <Modal show={showModalPlanosMes} onHide={() => setShowModalPlanosMes(false)} className='align-content-center'>
        <div className="modal-content">
          <div className="modal-header text-center" style={{ display: "block", paddingBottom: "0px" }}>
            <h5 className="modal-title" id="detalheSessoesLabel">Quantidade de Sessões por Planos</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body" style={{ maxHeight: "700px", overflowY: "auto" }}>
            <div className="col-md-12 container">
              {sessoesPorPlano.map((plano, i) => (
                <div key={i}>
                  <p><b>{plano.plano}</b>: {plano.sessoes}</p>

                  {sessoes
                    .filter((s) => s.planId === plano.plano_id)
                    .map((s, index) => {
                      const startDate = new Date(s.startDatetime.replace(' ', 'T'));
                      const endDate = new Date(s.endDatetime.replace(' ', 'T'));

                      return (
                        <p key={index} style={{ lineHeight: "0.5" }}>
                          <label>Data: </label> {startDate.toLocaleDateString("pt-BR")}{" "}
                          <label>Horário: </label> {startDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}{" "}
                          a {endDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      );
                    })}
                </div>
              ))}

              {avulsosLista.length > 0 && (
                <>
                  <p><b>Avulsos:</b></p>
                  {avulsosLista.map((a, i) => {
                    const startDate = new Date(a.startDatetime.replace(' ', 'T'));
                    const endDate = new Date(a.endDatetime.replace(' ', 'T'));

                    return (
                      <div key={i}>
                        <p><label>Título: </label> {a.title}</p>
                        <p style={{ lineHeight: "0.5" }}>
                          <label>Data: </label> {startDate.toLocaleDateString("pt-BR")}{" "}
                          <label>Horário: </label> {startDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}{" "}
                          a {endDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}{" "}
                          <label>Faturado: </label> {a.chargeable === 1 ? "Sim" : "Não"}
                        </p>
                        <hr />
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
          <div className="modal-footer container">
            <div className="col-md-12 text-center">
              <button type="button" className="btn btn-sm btn-info" data-bs-dismiss="modal" onClick={() => setShowModalPlanosMes(false)}>Fechar</button>
            </div>
          </div>
        </div>
      </Modal>

      {showModalExcluir && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar Exclusão</h5>
                {/* <button type="button" className="btn-close" onClick={() => setShowModalExcluir(false)} /> */}
              </div>
              <div className="modal-body">
                <p>Deseja realmente excluir o responsável <strong>{responsavelParaExcluir?.guardian?.name}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-info" onClick={() => setShowModalExcluir(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={handleExcluirResponsavel}>
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModalExclusaoLT && linhaTempoParaExcluir && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar exclusão</h5>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja excluir este item da linha do tempo?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-info" onClick={() => setShowModalExclusaoLT(false)}>Cancelar</button>
                <button className="btn btn-danger" onClick={excluirLinhaDoTempoConfirmado}>Excluir</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal foto do responsável */}
      <Modal show={!!fotoResponsavelModal} onHide={() => setFotoResponsavelModal(null)} centered>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {fotoResponsavelModal?.guardian?.name || 'Foto do responsável'}
            </h5>
            <button type="button" className="btn-close" onClick={() => setFotoResponsavelModal(null)} aria-label="Fechar" />
          </div>
          <div className="modal-body text-center p-3">
            {fotoResponsavelModal?.imageUrl && (
              <img
                src={fotoResponsavelModal.imageUrl}
                alt={fotoResponsavelModal.guardian?.name || 'Foto'}
                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '12px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div className="modal-footer justify-content-center">
            <button type="button" className="btn btn-sm btn-info" onClick={() => setFotoResponsavelModal(null)}>
              Fechar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de Confirmação Exclusão Paciente */}
      {showConfirmDeletePaciente && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar Exclusão</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmDeletePaciente(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja excluir este paciente?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => setShowConfirmDeletePaciente(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeletePaciente}
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PerfilPaciente